// The ONE shared rate formatter (AL2b; design-review money-format discipline). The
// wire value is always the `(0,1]` ratio STRING; the human sees a percentage. Do not
// re-decide this per screen.

/** The API's `rateRatio` regex — a decimal ratio, single leading digit, ≤4 places.
 *  The server `commission_rate_rate_chk` ((0,1]) is the authority; this is UX. */
export const RATE_RATIO_PATTERN = /^\d(\.\d{1,4})?$/;

/** The PERCENT the human types: 0–100 with ≤2 decimals. Capping the percent at 2
 *  decimals maps to exactly ≤4 ratio decimals — so a >2-decimal percent is REJECTED
 *  (shown as a field error), never silently rounded (the money rule). */
export const PERCENT_INPUT_PATTERN = /^\d{1,3}(\.\d{1,2})?$/;

/** ISO-8601 with an explicit timezone offset (mirrors `z.string().datetime({ offset:true })`). */
export const ISO_OFFSET_PATTERN =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;

/**
 * Convert a typed PERCENT to the `(0,1]` ratio wire string — EXACTLY, by decimal
 * shift (no float), so nothing is rounded. Returns null when the percent is malformed
 * or out of the (0,1] domain (i.e. ≤0% or >100%) — the caller shows the field error
 * and disables submit. "15" → "0.15"; "12.5" → "0.125"; "100" → "1"; "0" → null;
 * "150" → null; "12.555" → null (3 decimals; reject, don't round).
 */
export function percentToRatio(percentRaw: string): string | null {
  const p = percentRaw.trim();
  if (!PERCENT_INPUT_PATTERN.test(p)) return null;
  const [intPart, fracPart = ""] = p.split(".");
  const digits = intPart + fracPart;
  const ratioFrac = fracPart.length + 2; // ÷100 shifts the point two places left
  const padded = digits.padStart(ratioFrac + 1, "0");
  const cut = padded.length - ratioFrac;
  const intp = padded.slice(0, cut).replace(/^0+(?=\d)/, "");
  const frac = padded.slice(cut).replace(/0+$/, "");
  const ratio = frac ? `${intp}.${frac}` : intp;
  const n = Number(ratio);
  if (!(n > 0 && n <= 1) || !RATE_RATIO_PATTERN.test(ratio)) return null;
  return ratio;
}

/** Display a `(0,1]` ratio string as a Turkish-format percentage (`"0.15"` → `"%15,00"`).
 *  Display-only — the wire value is untouched. Deterministic (no ICU dependency). */
export function ratioToPercentDisplay(ratio: string): string {
  const pct = (Number(ratio) * 100).toFixed(2);
  return `%${pct.replace(".", ",")}`;
}
